import { initializeApp } from 'firebase/app';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { getUserStore } from '../store';
import EventBus from '@/layouts/slots/event-bus';
import config from '../config';
import { setJwtCookie } from "./cookies";
import { rg4js } from './raygun';
import { resolveAuthReady } from './auth/authReady';
import { initializeStatsig } from './statsig';

const firebaseConfig = {
    apiKey: config.VUE_APP_FIREBASE_API_KEY,
    authDomain: config.VUE_APP_FIREBASE_AUTH_DOMAIN,
    projectId: config.VUE_APP_FIREBASE_PROJECT_ID,
    storageBucket: config.VUE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: config.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: config.VUE_APP_FIREBASE_APP_ID,
    ...(config.VUE_APP_FIREBASE_MEASUREMENT_ID && {
        measurementId: config.VUE_APP_FIREBASE_MEASUREMENT_ID,
    }),
};

if (!(config.VUE_APP_ENV === 'production' || config.VUE_APP_ENV === 'prod') && config.VUE_APP_FIREBASE_APPCHECK_DEBUG_TOKEN) {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = config.VUE_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = config.VUE_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
}

// Initialize Firebase and get references to the services
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Firestore — lazy-loaded, only pulled in when first requested
let _firestoreDB = null;
async function getFirestoreDB() {
    if (!_firestoreDB) {
        const { getFirestore } = await import('firebase/firestore');
        _firestoreDB = getFirestore(app);
    }
    return _firestoreDB;
}

// App Check — lazy-loaded, not needed for initial render
import('firebase/app-check').then(({ initializeAppCheck, ReCaptchaEnterpriseProvider }) => {
    if (config.VUE_APP_RECAPTCHA_SITE_KEY) {
        initializeAppCheck(app, {
            provider: new ReCaptchaEnterpriseProvider(config.VUE_APP_RECAPTCHA_SITE_KEY),
            isTokenAutoRefreshEnabled: true,
        });
    }
});

async function handleAuthStateChange(user){
    const userStore = getUserStore();
    try {
        if (user) {
          // The profile load and the feature flags client only need the token,
          // so they start together. Statsig is identified from the cached
          // token's claims (no network) because the store learns _id from
          // /users/me, which is part of the load.
          const { claims } = await user.getIdTokenResult();
          const login = userStore.login({ user });
          initializeStatsig({ userID: claims.user, email: user.email }).catch((e) => rg4js('send', { error: e }));
          await login;
          // Set JWT cookie after successful login
          user.getIdToken().then(setJwtCookie).catch(console.error);
        }
      } catch (e) {
        rg4js('send', { error: e });
      } finally {
        resolveAuthReady();
        EventBus.emit('auth-statechanged', user);
      }
}

onIdTokenChanged(auth, user => {
    if (user) {
        user.getIdToken().then(jwt => {
            setJwtCookie(jwt)
        });
    }
});

export { auth, handleAuthStateChange, getFirestoreDB };